import { openDB } from 'idb';

const DB_NAME = 'concorsando-notifications-db';
const STORE_NAME = 'user-notifications';
const DB_VERSION = 4;

const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db, oldVersion, newVersion, transaction) {
    // Se non esiste lo store, crealo
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('by-user', 'userId', { unique: false });
      store.createIndex('by-date', 'timestamp', { unique: false });
    }
    
    // Store per configurazioni globali (es. utente attivo per il SW)
    if (!db.objectStoreNames.contains('sys-config')) {
      db.createObjectStore('sys-config');
    }

    // Migrazioni esistenti...
    const tx = transaction; // transaction è già passato in upgrade
    if (tx && tx.objectStoreNames.contains(STORE_NAME)) {
       const store = tx.objectStore(STORE_NAME);
       if (!store.indexNames.contains('by-user')) {
         store.createIndex('by-user', 'userId', { unique: false });
       }
       if (!store.indexNames.contains('by-date')) {
         store.createIndex('by-date', 'timestamp', { unique: false });
       }
    }
  },
});

export const NotificationStorage = {
  /**
   * Imposta l'utente attivo per il Service Worker
   */
  async setActiveUser(userId) {
    const db = await dbPromise;
    await db.put('sys-config', userId, 'activeUserId');
  },

  /**
   * Rimuove l'utente attivo
   */
  async clearActiveUser() {
    const db = await dbPromise;
    await db.delete('sys-config', 'activeUserId');
  },

  /**
   * Recupera tutte le notifiche per uno specifico utente
   * @param {string} userId 
   */
  async getByUser(userId) {
    if (!userId) return [];
    const db = await dbPromise;
    
    // Cerchiamo sia come stringa che come numero per retrocompatibilità
    const resultsStr = await db.getAllFromIndex(STORE_NAME, 'by-user', String(userId));
    let all = [...resultsStr];
    
    if (!isNaN(userId) && typeof userId !== 'boolean') {
        const resultsNum = await db.getAllFromIndex(STORE_NAME, 'by-user', Number(userId));
        // Evita duplicati se per qualche motivo String(userId) === Number(userId) in IDB (raro)
        resultsNum.forEach(item => {
            if (!all.find(a => a.id === item.id)) {
                all.push(item);
            }
        });
    }

    // Ordina per data decrescente (dal più recente)
    return all.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  },

  /**
   * Salva una notifica
   * @param {Object} notification 
   */
  async save(notification) {
    const db = await dbPromise;
    // Assicuriamoci che ci sia un ID e un timestamp
    const item = {
      id: notification.id || Date.now().toString(36) + Math.random().toString(36).substr(2),
      timestamp: notification.timestamp || new Date().toISOString(),
      read: false,
      ...notification
    };
    if (item.userId) {
      item.userId = String(item.userId);
    }
    await db.put(STORE_NAME, item);

    // Manteniamo solo le ultime 50 notifiche per utente
    if (item.userId) {
      const all = await db.getAllFromIndex(STORE_NAME, 'by-user', String(item.userId));
      if (all.length > 50) {
        // Ordina per data crescente (i più vecchi prima)
        all.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        // Calcola quanti rimuoverne
        const toDeleteCount = all.length - 50;
        const toDelete = all.slice(0, toDeleteCount);

        // Cancella i più vecchi
        const tx = db.transaction(STORE_NAME, 'readwrite');
        await Promise.all(toDelete.map(n => tx.store.delete(n.id)));
        await tx.done;
      }
    }
    
    return item.id;
  },

  /**
   * Segna una notifica come letta
   * @param {string} id 
   */
  async markAsRead(id) {
    const db = await dbPromise;
    const notification = await db.get(STORE_NAME, id);
    if (notification) {
      notification.read = true;
      await db.put(STORE_NAME, notification);
    }
  },

  /**
   * Rimuove tutte le notifiche di un certo tipo per un utente
   * @param {string} userId 
   * @param {string|number} type 
   */
  async removeByType(userId, type) {
    const db = await dbPromise;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const index = tx.store.index('by-user');
    
    let cursor = await index.openCursor(IDBKeyRange.only(String(userId)));
    while (cursor) {
      const notification = cursor.value;
      const notifType = String(notification.data?.type || notification.type || null);
      if (notifType === String(type)) {
        await cursor.delete();
      }
      cursor = await cursor.continue();
    }
    await tx.done;
  },

  /**
   * Cancella notifiche di un utente (opzionale, es. su richiesta GDPR)
   */
  async clearByUser(userId) {
    const db = await dbPromise;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const index = tx.store.index('by-user');
    
    // Itera e cancella (non c'è deleteByIndex diretto standard veloce in IDB v1/v2 senza cursori)
    let cursor = await index.openCursor(IDBKeyRange.only(userId));
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
  }
};
